/* ====== Perform a standard search ====== */
class SearchForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    if (!this.input) return;

    // Reset Content
    this.input.form.addEventListener("reset", this.onFormReset.bind(this));
    // Listen for the input event
    this.input.addEventListener(
      "input",
      webvista.debounce((event) => {
        this.onChange(event);
      }, 300),
    );

    // Pop up the search container
    if (this.hasAttribute("data-pop-panel")) {
      this.input.addEventListener("focusin", this.onFocusin.bind(this));
      this.addEventListener("focusout", this.onFocusout.bind(this));
    }
  }

  /* ====== Reset button; toggle display status ====== */
  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains("hidden");
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove("hidden");
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add("hidden");
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  /* ====== Reset form ====== */
  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();

    this.input.value = "";
    this.input.focus();
    // Modifying `input.value` via JavaScript does not trigger the `input` event; it must be triggered manually.
    this.input.dispatchEvent(
      new Event("input", {
        bubbles: true,
        cancelable: true,
      }),
    );
    this.toggleResetButton();
  }

  /* ====== The input box gains focus. ====== */
  onFocusin() {
    this.classList.add("in-focus");
    this.isOpen = true;
    this.input.setAttribute("aria-expanded", "true");
    // Activate the header; if the header has a transparent background, this enables the display of a background color.
    this.toggleHeaderActive(true);
  }

  /* ====== Search lost focus ====== */
  onFocusout() {
    // It is necessary to retrieve the latest focused element asynchronously because there is a delay in focus updates.
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      this.classList.remove("in-focus");
      this.isOpen = false;
      this.input.setAttribute("aria-expanded", "false");
      this.toggleHeaderActive(false);
    });
  }

  /* ====== Toggle header state ====== */
  toggleHeaderActive(active = true) {
    this.header = this.header || this.closest(".section-header");
    if (this.header) this.header.classList.toggle("header--is-active", active);
  }
}

customElements.define("search-form", SearchForm);

/* ====== Main search class :: Handles synchronization; synchronizes search content when a page contains other search components. ====== */
class MainSearch extends SearchForm {
  connectedCallback() {
    super.connectedCallback();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
  }

  onFormReset(event) {
    super.onFormReset(event);
    this.keepInSync("", this.input);
  }

  /* ====== Input change ====== */
  onChange(event) {
    super.onChange();
    this.keepInSync(event.target.value, this.input);
  }

  /* ====== Synchronous search ====== */
  keepInSync(value, from) {
    this.allSearchInputs.forEach((input) => {
      if (input !== from) input.value = value;
    });
  }
}

customElements.define("main-search", MainSearch);

/* ====== Predictive search ====== */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    // Search keywords
    this.searchTerm = "";
    this.isOpen = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.searchResultsWrapper = this.querySelector(".search-results-wrapper");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.querySelectorAll(".popular-searches a").forEach((suggestion) => {
      suggestion.addEventListener(
        "click",
        this.onPopularKeywordClick.bind(this),
      );
    });

    this.abortController = new AbortController();
    // Watch for the Search-Drawer opening, then auto-trigger search
    // if the input already has a pre-filled value (e.g. came from search page)
    this._drawerObserver = new MutationObserver(() => {
      const drawer = document.getElementById('Search-Drawer');
      if (!drawer) return;

      const isOpen = drawer.hasAttribute('open');
      if (!isOpen) return;

      const prefilled = this.getQuery();
      if (!prefilled) return;

      // Drawer just opened with a pre-filled value — wait for it to finish
      // rendering (so getBoundingClientRect returns real values), then fetch
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.searchTerm = prefilled;
          if (this.getAttribute('results') === 'true') {
            this.showResults();
          } else {
            this.showPlaceholder();
            this.getSearchResults();
          }
        });
      });
    });

    const drawer = document.getElementById('Search-Drawer');
    if (drawer) {
      this._drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
    if (this._drawerObserver) this._drawerObserver.disconnect();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  /* ====== Get the content of the input box ====== */
  getQuery() {
    return this.input.value.trim();
  }

  /* ====== Handle input field content changes ====== */
  onChange() {
    super.onChange();

    const currentSearchTerm = this.getQuery();
    if (currentSearchTerm === this.searchTerm) return;
    this.searchTerm = currentSearchTerm;

    if (this.searchTerm.length <= 0) return this.closeResults(true);

    this.showPlaceholder(); // Show placeholders
    this.getSearchResults(); // Get search results
  }

  /* ====== Keyword click ====== */
  onPopularKeywordClick(event) {
    event.preventDefault(); // Prevent the default action of the <a> link.

    const keyword = event.currentTarget.getAttribute("data-keyword");
    if (keyword !== "") {
      this.input.value = keyword;
      this.input.focus();
      // Modifying `input.value` via JavaScript does not trigger the `input` event; it must be triggered manually.
      this.input.dispatchEvent(
        new Event("input", {
          bubbles: true,
          cancelable: true,
        }),
      );
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    this.searchTerm = "";
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  /* ====== Search Gain focus ====== */
  onFocusin() {
    super.onFocusin();

    // To display search data, keywords need to be retrieved again, as search terms may change.
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm && currentSearchTerm === this.searchTerm) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      // Show results
      this.showResults();
    } else {
      // Search again
      this.getSearchResults();
    }
  }

  /* ====== Displays the placeholder :: Shown when performing a new search. ====== */
  showPlaceholder() {
    this.placeholder = this.placeholder || this.querySelector(`#Placeholder-Search-Form-${this.dataset.section}`).innerHTML;
    this.searchResultsWrapper.innerHTML = this.placeholder;
  }

  /* ====== Retrieve search results :: Use cached data to improve efficiency ====== */
  getSearchResults() {
    const queryKey = this.searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoading();

    // Use cached data directly.
    if (this.cachedResults[queryKey])
      return this.renderSearchResults(this.cachedResults[queryKey]);

    // Re-query data
    const url = new URL(
      window.routes.predictive_search_url,
      window.location.origin,
    );

    url.search = new URLSearchParams({
      q: this.searchTerm, // It will automatically encode.
      "resources[limit]": 4,
      "resources[limit_scope]": "each",
      section_id: "predictive-search",
    });

    webvista
      .fetchHtml(
        url.toString(),
        this.abortController.signal,
      )
      .then((html) => {
        const shopifySection = html.querySelector(".shopify-section");
        let resultsMarkup = shopifySection ? shopifySection.innerHTML : "";
        if (!resultsMarkup) throw new Error("No search results found");

        resultsMarkup = this.preventDuplicatedIDs(resultsMarkup); // Prevent duplicate IDs

        // Synchronization results
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.removeAttribute("loading");
        this.searchResultsWrapper.innerHTML = " ";
        this.removeAttribute("results");
      });
  }

  /* ====== Render search results ====== */
  renderSearchResults(resultsHtml) {
    this.searchResultsWrapper.innerHTML = resultsHtml;
    this.setAttribute("results", "true");
    this.setLiveRegionResults();
    this.showResults();
    webvista.initLazyImages();
    webvista.initTooltips();
  }

  /* ====== Gets the maximum height of the container for predicted search results ====== */
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - this.querySelector(".predictive-search-main").getBoundingClientRect().top - 32;
    return this.resultsMaxHeight;
  }

  /* ====== Displays predictive search content :: The 'open' attribute controls visibility ====== */
  showResults() {
    // Recalculate height whenever panel is closed — element may have been
    // hidden (display:none / visibility:hidden) so prior measurement is stale
    if (!this.hasAttribute('open')) {
      this.resultsMaxHeight = false;
    }

    this.style.setProperty(
      '--result-max-height',
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`,
    );
    this.setAttribute('open', 'true');

    if (typeof window.initDynamicPrices === 'function') {
      window.initDynamicPrices(document);
    }

    if (window.yotpoWidgetsContainer) {
      window.yotpoWidgetsContainer.initWidgets();
    }

    window.FlairApp.init();
  }

  /* ====== Close the prediction results.
   * If the search data is not cleared, the previous search data will be displayed immediately the next time it is opened.
   * The `open` attribute controls the visibility of the results container, 
   * while the `results` attribute controls the presence of data. ====== */
  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.searchResultsWrapper.innerHTML = " "; // leave a space to avoid display none
      this.removeAttribute("results");
    }
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.resultsMaxHeight = false;
    this.style.removeProperty("--result-max-height");
  }

  /* ====== Prevent duplicate IDs ====== */
  preventDuplicatedIDs(html) {
    if (!this.id) return html;

    return html.replace(
      /<[^>]+>/g,
      (match) => {
        // If Flair badge container exists, do nothing
        if (match.includes('flair-badge-container')) {
          return match;
        }
        return match.replace(
          /(id|aria-controls|aria-labelledby)="([^"]+)"/g,
          (subMatch, attr, value) => {
            return `${attr}="${value}_${this.id}"`;
          }
        );
      }
    );
  }

  /* ====== Loading state ====== */
  setLiveRegionLoading() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");
    this.setAttribute("loading", "true");
    this.setLiveRegionText(this.loadingText);
  }

  /* ====== Assistive devices display search results. ====== */
  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(this.querySelector("[data-predictive-search-live-region-count-value]").textContent);
  }

  /* ====== Auxiliary device search status display ====== */
  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }
}

customElements.define("predictive-search", PredictiveSearch);